import {FC, useEffect, useState} from 'react'
import {api} from '../services/api'

/**
 * Renders a private file (GET /api/files/:key needs a Bearer token an <img>
 * can't send). Fetches it with auth, shows it via a blob URL, and revokes on
 * unmount/key change. Handles images and PDFs. With `zoomable`, clicking opens
 * a full-screen preview (image or inline PDF viewer); Esc or click closes.
 */
export const AuthedImage: FC<{
  fileKey: string
  alt?: string
  style?: React.CSSProperties
  className?: string
  zoomable?: boolean
}> = ({fileKey, alt = '', style, className, zoomable}) => {
  const [url, setUrl] = useState<string | null>(null)
  const [type, setType] = useState('')
  const [error, setError] = useState(false)
  const [zoom, setZoom] = useState(false)

  const isPdf = type === 'application/pdf'

  useEffect(() => {
    let revoked: string | null = null
    let active = true
    setUrl(null)
    setError(false)
    api
      .fileBlobUrl(fileKey)
      .then(({url: u, type: t}) => {
        if (active) {
          revoked = u
          setUrl(u)
          setType(t)
        } else {
          URL.revokeObjectURL(u)
        }
      })
      .catch(() => active && setError(true))
    return () => {
      active = false
      if (revoked) URL.revokeObjectURL(revoked)
    }
  }, [fileKey])

  useEffect(() => {
    if (!zoom) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setZoom(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [zoom])

  if (error) {
    return (
      <div style={{...style, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f4f6fa', color: '#aab0bf', fontSize: 13}}>
        Couldn't load
      </div>
    )
  }
  if (!url) {
    return <div style={{...style, background: '#f1f3f7'}} />
  }

  const openable = zoomable || isPdf // PDFs always open in a viewer
  const onOpen = openable ? () => setZoom(true) : undefined

  return (
    <>
      {isPdf ? (
        <div
          onClick={onOpen}
          style={{...style, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, background: '#f8f9fc', color: '#475569', cursor: 'zoom-in'}}
        >
          <span style={{fontSize: 30}}>📄</span>
          <span style={{fontSize: 12, fontWeight: 600}}>PDF — click to view</span>
        </div>
      ) : (
        <img
          src={url}
          alt={alt}
          className={className}
          onClick={onOpen}
          style={{...style, cursor: openable ? 'zoom-in' : style?.cursor}}
        />
      )}

      {zoom && (
        <div
          onClick={() => setZoom(false)}
          style={{position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(8,10,14,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24}}
        >
          <button
            onClick={(e) => {e.stopPropagation(); setZoom(false)}}
            aria-label='Close'
            style={{position: 'absolute', top: 18, right: 20, width: 44, height: 44, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.14)', color: '#fff', fontSize: 22, cursor: 'pointer', zIndex: 1}}
          >
            ✕
          </button>
          {isPdf ? (
            <iframe
              title={alt || 'PDF'}
              src={url}
              onClick={(e) => e.stopPropagation()}
              style={{width: '92vw', height: '90vh', border: 'none', borderRadius: 8, background: '#fff'}}
            />
          ) : (
            <img src={url} alt={alt} onClick={(e) => e.stopPropagation()} style={{maxWidth: '92vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 10, boxShadow: '0 20px 60px rgba(0,0,0,0.6)'}} />
          )}
        </div>
      )}
    </>
  )
}
